import { Page, PageHero, Spinner } from "src/components/common";
import React, { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	ITrainingCourse,
	TrainingCourse,
	TrainingCourseEnrollment,
} from "src/@types/ImpiricaPlatformApi";
import { useApi } from "src/Api/useApi";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { FormComponentProps } from "antd/lib/form";
import { toast } from "react-toastify";
import AddEditCourseForm from "./AddEditCourseForm";

interface AddEditCoursesPageProps extends FormComponentProps {
	match?: any;
}

const AddEditCoursesPage: FC<AddEditCoursesPageProps> = ({ match }) => {
	const courseId = Number(match.params.trainingCourseId);
	const isNew = courseId <= 0;

	const { trainingCourseClient } = useApi();

	const [loading, setLoading] = useState<boolean>(true);
	const [trainingCourse, setTrainingCourse] = React.useState<ITrainingCourse>({
		id: 0,
		isActive: true,
		autoEnrollAll: true,
	});

	useEffect(() => {
		if (trainingCourseClient && courseId > 0) {
			trainingCourseClient
				.getTrainingCourse(courseId)
				.then(setTrainingCourse)
				.catch(() => toast.error("Unable to load training course information"))
				.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}
	}, [trainingCourseClient, courseId]);

	const handleSave = (course: ITrainingCourse) => {
		if (isNew) {
			addNewCourse(course);
		} else {
			updateCourse(course);
		}
	};

	const addNewCourse = async (course: ITrainingCourse) => {
		if (!trainingCourseClient) throw new Error("No training course client");

		try {
			const id = await trainingCourseClient.addTrainingCourse(
				new TrainingCourse({ ...course })
			);
			setTrainingCourse({
				...course,
				id,
			});
			toast.success(
				`Successfully ${isNew ? "added" : "updated"} training course`,
				{
					onClose: () => {
						window.location.href = "/admin/training";
					},
				}
			);
		} catch (error) {
			console.error(error);
			toast.error("Unable to save training course information");
		}
	};

	const updateCourse = async (course: ITrainingCourse) => {
		if (!trainingCourseClient) throw new Error("No training course client");

		try {
			await trainingCourseClient.updateTrainingCourse(
				course.id,
				new TrainingCourse({
					...course,
					enrollments: course.enrollments
						? course.enrollments.map(
								(e) => new TrainingCourseEnrollment({ ...e })
						  )
						: [],
				})
			);
			setTrainingCourse({ ...course });
			toast(`Successfully ${isNew ? "added" : "updated"} training course`, {
				type: "success",
				onClose: () => {
					window.location.href = "/admin/training";
				},
			});
		} catch (error) {
			console.error(error);
			toast.error("Unable to save training course information");
		}
	};

	return loading ? (
		<Spinner />
	) : (
		<Page title="Training Course Details">
			<PageHero>
				<Link to="/admin/training">&lt; TRAINING COURSES</Link>
				<h1 className="mb-0">{isNew ? "Add New Course" : "Edit Course"}</h1>
			</PageHero>

			<AuthorizationWrapper
				type="hide"
				permissions={[AdminPermissions.GlobalAdmin]}
			>
				<div className="container-fluid">
					<AddEditCourseForm value={trainingCourse} onChange={handleSave} />
				</div>
			</AuthorizationWrapper>
		</Page>
	);
};

export default AddEditCoursesPage;
